import {
  computeClosestFrame,
  computePlaneDistanceRanges,
  formatPlaneRange,
  getDirectionLabel,
  signedDistanceToPlane,
} from 'features/kestrelViewer/utils/referencePlaneUtils'
import {
  convertFromCentimeters,
  getUnitLabel,
  type Unit,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import {
  KVAnnotation,
  KVInspectionArea,
  KVOrientationAxes,
  KVReferencePlane,
} from 'types'
import { ReferencePlaneAlias } from './KVReferencePlane'

export interface URO3Defect {
  id: string
  annotationId: string | null
  annotationLabel: string | null
  included: boolean
  itemNo: string
  structureCategory: string
  defectType: string
  length: string
  width: string
  depth: string
  inspectionArea: string
  locationTransverse: string
  locationLongitudinal: string
  locationVertical: string
  repairMethod: string
  drawing: string
  pcNumber: string
  sketchLink: string
  remarks: string
}

export interface URO3FormData {
  shipName: string
  compartmentName: string
  inspectionDates: string
  assessment: string | null
  defects: URO3Defect[]
}

export interface DefectModelContext {
  referencePlanes: KVReferencePlane[]
  framePlanes?: KVReferencePlane[]
  measurementScale: number
  defaultUnit: Unit
  orientationAxes?: KVOrientationAxes | null
  inspectionAreas?: KVInspectionArea[]
}

export const createDefaultURO3FormData = (): URO3FormData => ({
  shipName: '',
  compartmentName: '',
  inspectionDates: '',
  assessment: null,
  defects: [],
})

const DEFECT_TYPE_MAP: Record<string, string> = {
  'Crack-like indications': 'C',
  'Deformation': 'D',
  'Weld or corrosive undercutting': 'U',
  'Pitting': 'P',
  'General corrosion': 'GC',
}

function mapDefectType(annotationDefectType: string): string {
  return DEFECT_TYPE_MAP[annotationDefectType] ?? ''
}

const ALIAS_TO_FIELD: Record<
  ReferencePlaneAlias,
  'locationTransverse' | 'locationLongitudinal' | 'locationVertical'
> = {
  [ReferencePlaneAlias.TRANSVERSE]: 'locationTransverse',
  [ReferencePlaneAlias.LONGITUDINAL]: 'locationLongitudinal',
  [ReferencePlaneAlias.VERTICAL]: 'locationVertical',
}

function formatDimension(
  unrealValue: number | undefined,
  measurementScale: number,
  unit: Unit
): string {
  if (unrealValue === undefined || unrealValue === null || isNaN(unrealValue))
    return ''
  const cm = unrealValue * measurementScale
  const converted = convertFromCentimeters(cm, unit)
  return `${converted.toFixed(2)} ${getUnitLabel(unit)}`
}

function computeLocations(
  annotation: KVAnnotation,
  ctx: DefectModelContext
): Record<string, string> {
  const result: Record<string, string> = {}

  const points =
    annotation.geometry?.kind === 'polygon'
      ? annotation.geometry.points
      : undefined
  const pos = annotation.centeredPosition
  const effectivePoints = points && points.length > 0
    ? points
    : pos ? [pos] : []

  if (effectivePoints.length === 0) return result

  const convert = (cm: number) => convertFromCentimeters(cm, ctx.defaultUnit)
  const unitLabel = getUnitLabel(ctx.defaultUnit)

  if (effectivePoints.length > 1) {
    const ranges = computePlaneDistanceRanges(
      effectivePoints,
      ctx.referencePlanes,
      ctx.measurementScale
    )

    for (const range of ranges) {
      const alias = range.alias as Exclude<typeof range.alias, ''>
      if (!alias) continue
      const field = ALIAS_TO_FIELD[alias as keyof typeof ALIAS_TO_FIELD]
      if (!field || result[field]) continue

      const plane = ctx.referencePlanes.find((p) => p.id === range.planeId)
      const formatted = formatPlaneRange(
        range,
        convert,
        unitLabel,
        ctx.orientationAxes,
        plane?.normal
      )

      if (formatted.straddles) {
        result[field] = `${formatted.distance} of ${range.planeLabel}`
      } else {
        result[field] = formatted.directionLabel
          ? `${formatted.distance} ${formatted.directionLabel} of ${range.planeLabel}`
          : `${formatted.distance} from ${range.planeLabel}`
      }
    }

    if (ctx.framePlanes && ctx.framePlanes.length >= 2 && pos) {
      const frameRange = computeClosestFrame(
        pos,
        effectivePoints,
        ctx.framePlanes,
        ctx.measurementScale
      )
      if (frameRange) {
        const frame = ctx.framePlanes.find((f) => f.id === frameRange.planeId)
        const formatted = formatPlaneRange(
          frameRange,
          convert,
          unitLabel,
          ctx.orientationAxes,
          frame?.normal
        )

        const frameAlias = ctx.framePlanes[0]?.annotationAlias as keyof typeof ALIAS_TO_FIELD
        const frameField = frameAlias ? ALIAS_TO_FIELD[frameAlias] : undefined

        let frameStr: string
        if (formatted.straddles) {
          frameStr = `${formatted.distance} of ${frameRange.planeLabel}`
        } else {
          frameStr = formatted.directionLabel
            ? `${formatted.distance} ${formatted.directionLabel} of ${frameRange.planeLabel}`
            : `${formatted.distance} from ${frameRange.planeLabel}`
        }

        if (frameField) {
          result[frameField] = result[frameField]
            ? `${frameStr}, ${result[frameField]}`
            : frameStr
        }
      }
    }
  } else {
    for (const plane of ctx.referencePlanes) {
      if (!plane.showInAnnotation || !plane.origin || !plane.normal) continue
      if (plane.isFrame) continue
      const alias = plane.annotationAlias
      if (!alias) continue
      const field = ALIAS_TO_FIELD[alias]
      if (!field || result[field]) continue

      const signed = signedDistanceToPlane(
        effectivePoints[0],
        plane.origin,
        plane.normal,
        ctx.measurementScale
      )
      const converted = convert(Math.abs(signed))
      const direction = ctx.orientationAxes
        ? getDirectionLabel(plane.normal, signed, ctx.orientationAxes)
        : undefined

      result[field] = direction
        ? `${converted.toFixed(2)} ${unitLabel} ${direction} of ${plane.label}`
        : `${converted.toFixed(2)} ${unitLabel} from ${plane.label}`
    }
  }

  return result
}

export const createDefectFromAnnotation = (
  annotation: KVAnnotation,
  ctx?: DefectModelContext
): URO3Defect => {
  const nmlAnno = annotation.navyMultiLayerAnnotation
  const locations = ctx ? computeLocations(annotation, ctx) : {}

  const nmlLength = nmlAnno?.length || ''
  const nmlWidth = nmlAnno?.width || ''
  const autoLength =
    nmlLength ||
    (ctx ? formatDimension(annotation.boundingBoxLength, ctx.measurementScale, ctx.defaultUnit) : '')
  const autoWidth =
    nmlWidth ||
    (ctx ? formatDimension(annotation.boundingBoxWidth, ctx.measurementScale, ctx.defaultUnit) : '')

  const inspectionAreaId = annotation.inspectionArea || ''
  const inspectionArea =
    inspectionAreaId && ctx?.inspectionAreas
      ? (ctx.inspectionAreas.find((ia) => ia.id === inspectionAreaId)?.label ??
        inspectionAreaId)
      : inspectionAreaId

  if (nmlAnno) {
    return {
      id: crypto.randomUUID(),
      annotationId: annotation.id,
      annotationLabel: annotation.label || null,
      included: true,
      itemNo: '',
      structureCategory: nmlAnno.structureCategory || '',
      defectType: mapDefectType(nmlAnno.defectType || ''),
      length: autoLength,
      width: autoWidth,
      depth: nmlAnno.depth || '',
      inspectionArea,
      locationTransverse:
        nmlAnno.locationTransverse || locations.locationTransverse || '',
      locationLongitudinal:
        nmlAnno.locationLongitudinal || locations.locationLongitudinal || '',
      locationVertical:
        nmlAnno.locationVertical || locations.locationVertical || '',
      repairMethod: '',
      drawing: '',
      pcNumber: '',
      sketchLink: '',
      remarks: '',
    }
  }

  return {
    id: crypto.randomUUID(),
    annotationId: annotation.id,
    annotationLabel: annotation.label || null,
    included: true,
    itemNo: '',
    structureCategory: '',
    defectType: '',
    length: autoLength,
    width: autoWidth,
    depth: '',
    inspectionArea,
    locationTransverse: locations.locationTransverse || '',
    locationLongitudinal: locations.locationLongitudinal || '',
    locationVertical: locations.locationVertical || '',
    repairMethod: '',
    drawing: '',
    pcNumber: '',
    sketchLink: '',
    remarks: annotation.notes || '',
  }
}
